import type { ReactNode, SyntheticEvent } from "react"
import { useCallback } from "react"
import { useSearchParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { Box, Divider, Stack, Tab, Tabs } from "@mui/material"

import { TAB_ICONS } from "helpers/config/tabs/tabIconsConfig"
import { AlertSettingDrawerProvider } from "widgets/alerts/settings/AlertSettingDrawer"
import { SectionContainer } from "widgets/styled/containers"

const VIEW_PARAM = "view"

export type AlertsView = "configuredAlerts" | "activity"

// First entry is the default view, the one shown without the `view` param
const ALERTS_VIEWS: { view: AlertsView; labelKey: string }[] = [
  { view: "configuredAlerts", labelKey: "tabs.CONFIGURED_ALERTS" },
  { view: "activity", labelKey: "tabs.ACTIVITY" },
]

const DEFAULT_VIEW = ALERTS_VIEWS[0].view

interface AlertsTabViewProps {
  tabsConfig: Record<AlertsView, ReactNode>
  // Shown next to the tabs while their view is open.
  actions?: Partial<Record<AlertsView, ReactNode>>
}

const AlertsTabView = ({ tabsConfig, actions }: AlertsTabViewProps) => {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const viewParam = searchParams.get(VIEW_PARAM) as AlertsView | null
  const view = viewParam && viewParam in tabsConfig ? viewParam : DEFAULT_VIEW

  const handleChange = useCallback(
    (_event: SyntheticEvent, nextView: AlertsView) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          if (nextView === DEFAULT_VIEW) next.delete(VIEW_PARAM)
          else next.set(VIEW_PARAM, nextView)
          return next
        },
        { replace: true },
      )
    },
    [setSearchParams],
  )

  return (
    <AlertSettingDrawerProvider>
      <SectionContainer disableGutters maxWidth="xl" sx={{ p: 0, overflow: "hidden" }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          gap={{ xs: 1, sm: 2 }}
          sx={{ px: { xs: 1, sm: 2 }, pt: 0.5 }}
        >
          <Tabs
            value={view}
            onChange={handleChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              minHeight: "auto",
              "& .MuiTabs-scrollButtons": { width: 20 },
              "& .MuiTab-root": {
                minHeight: "auto",
                minWidth: { xs: 0, sm: 90 },
                px: { xs: 1, sm: 2 },
                py: 1.25,
                textTransform: "none",
                fontWeight: 500,
                color: "text.secondary",
              },
              "& .Mui-selected": { color: "primary.main" },
            }}
          >
            {ALERTS_VIEWS.map(({ view: tabView, labelKey }) => (
              <Tab
                key={tabView}
                icon={TAB_ICONS[labelKey]}
                iconPosition="start"
                label={t(labelKey)}
                value={tabView}
              />
            ))}
          </Tabs>
          {actions?.[view]}
        </Stack>
        <Divider />
        <Box
          sx={{
            p: { xs: "0.5rem 0.5rem 1rem", md: "0.5rem 1.5rem 1rem" },
            "& > .MuiContainer-root": {
              border: "none",
              margin: 0,
              padding: 0,
              backgroundColor: "transparent",
              borderRadius: 0,
            },
          }}
        >
          {tabsConfig[view]}
        </Box>
      </SectionContainer>
    </AlertSettingDrawerProvider>
  )
}

export default AlertsTabView
